// Normalized voucher error codes → i18n keys.
// The backend maps every provider's raw error onto one of these codes; the code is the only thing
// that crosses the wire. Provider error text must never reach a buyer — IC Mega and Tmura both
// return strings of varying quality that change without notice.
export const VOUCHER_ERROR_KEYS = {
  VOUCHER_ALREADY_USED: 'voucher_already_used',
  VOUCHER_NOT_FOUND: 'voucher_not_found',
  VOUCHER_INVALID: 'voucher_invalid',
  VOUCHER_NOT_FOR_THIS_EVENT: 'voucher_not_for_this_event',
  VOUCHER_NOTHING_TO_DISCOUNT: 'voucher_nothing_to_discount',
  VOUCHER_ALREADY_ADDED: 'voucher_already_added',
  VOUCHER_PROVIDER_UNAVAILABLE: 'voucher_provider_unavailable',
  VOUCHER_REDEEM_FAILED_ORDER_VOIDED: 'voucher_redeem_failed_order_voided',
  VOUCHER_REDEEM_FAILED_REFUNDED: 'voucher_redeem_failed_refunded',
};

const FALLBACK_ERROR_KEY = VOUCHER_ERROR_KEYS.VOUCHER_PROVIDER_UNAVAILABLE;

// The real check route (feat/voucher-3-check) returns the SHORT provider-level codes. Map them onto
// the same i18n keys as the long VOUCHER_* forms so either shape translates.
const PROVIDER_ERROR_ALIASES = {
  ALREADY_USED: VOUCHER_ERROR_KEYS.VOUCHER_ALREADY_USED,
  NOT_FOUND: VOUCHER_ERROR_KEYS.VOUCHER_NOT_FOUND,
  INVALID: VOUCHER_ERROR_KEYS.VOUCHER_INVALID,
  TRANSIENT: VOUCHER_ERROR_KEYS.VOUCHER_PROVIDER_UNAVAILABLE,
  UNKNOWN: VOUCHER_ERROR_KEYS.VOUCHER_PROVIDER_UNAVAILABLE,
  // Core-synthesized for eligibility-only providers: the per-ID cap on this event is used up.
  CAP_REACHED: 'voucher_cap_reached',
};

// Per-provider UI behavior, keyed by providerType (the public grid already exposes it — no BE
// change). Providers WITHOUT an entry get today's behavior everywhere, which is what keeps
// icmega/tmura byte-identical. `eligibilityOnly` marks clubs where the provider only answers
// yes/no and nothing is burned for unselected tickets — so the #1039/#1042 coverage-mismatch
// warnings are noise and are skipped.
export const PROVIDER_UI_HINTS = {
  digitel: {
    codeLabelKey: 'voucher_enter_id_number', // the "code" is a 9-digit Israeli ID (ת"ז)
    inputMode: 'numeric',
    maxLength: 9,
    // The BE strips separators, left-pads to 9 and checksum-validates; anything outside 8-9 digits
    // is rejected there. Catch it here first so the buyer gets the ID-specific message instantly
    // instead of a round-trip, and so a typo never spends a provider call.
    codePattern: /^\d{8,9}$/,
    eligibilityOnly: true,
    errorOverrides: {
      NOT_FOUND: 'voucher_not_found_digitel', // "not a Digitel member", not "order not found"
      INVALID: 'voucher_invalid_id_number',
    },
  },
};

// Look hints up through the canonical identity: a club saved as "Digitel" (the club dialog keeps
// whatever value it was given, and the BE may serve any case) must get the same input, the same
// copy and the same eligibility-only behavior as "digitel".
export function providerUiHints(providerType) {
  return PROVIDER_UI_HINTS[normalizeProviderType(providerType)] || {};
}

export const isEligibilityOnlyProvider = (providerType) =>
  Boolean(providerUiHints(providerType).eligibilityOnly);

// Format gate for providers that declare one; everything else accepts any non-empty code.
export function isValidVoucherCodeFormat(providerType, code) {
  const pattern = providerUiHints(providerType).codePattern;
  if (!pattern) return true;
  return pattern.test((code || '').toString().trim());
}

export function resolveVoucherErrorKey(code, providerType) {
  return (
    providerUiHints(providerType).errorOverrides?.[code] ||
    VOUCHER_ERROR_KEYS[code] ||
    PROVIDER_ERROR_ALIASES[code] ||
    FALLBACK_ERROR_KEY
  );
}

// The check response carries its error as { error: { code, message } }; older/other shapes use a
// flat `error` string or `errorCode`. Pull the code string out of whichever shape arrived.
export function voucherErrorCodeOf(body) {
  const err = body?.error;
  if (err && typeof err === 'object') return err.code;
  return err || body?.errorCode;
}

// The grid renders whatever the seller account exposes — adding provider #N needs no FE change.
// The PUBLIC grid route already filters to active clubs and omits `isActive` entirely, so treat a
// missing flag as active; only an explicit `isActive: false` (admin list) is dropped.
export function activeVoucherSources(voucherSources) {
  return (voucherSources || [])
    .filter((source) => source.isActive !== false)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

// On a time-slot event the slot is chosen before redeeming, so a redemption is always checked
// against a concrete slot's rows. (No provider-side hold happens at check time — the backend
// defines an IC Mega `reserve` adapter method but never calls it; the only provider mutation
// is the burn at order finalization.)
export function canOpenRedeemDialog({ enabled, voucherSources, isTimeSlotEvent, chosenTimeSlot }) {
  // Per-event opt-in: the producer switches voucher redemption on for this event (event.showVoucherRedemption).
  // Only an explicit false blocks; omitting `enabled` stays non-blocking so callers can adopt it gradually.
  if (enabled === false) {
    return { allowed: false };
  }
  if (activeVoucherSources(voucherSources).length === 0) {
    return { allowed: false };
  }
  if (isTimeSlotEvent && !chosenTimeSlot) {
    return { allowed: false, reasonKey: 'voucher_choose_slot_first' };
  }
  return { allowed: true };
}

export class VoucherError extends Error {
  constructor(code) {
    super(code);
    this.name = 'VoucherError';
    this.code = code;
  }
}

const voucherKey = ({ voucherSourceId, code }) => `${voucherSourceId}::${code}`;

// Redemption accumulates: IC Mega issues one variant per barcode, so a buyer entitled to a child
// and an adult holds two barcodes and enters them one after another. Each entry carries the
// `redemptionId` that `check` returns (the backend persists a voucher-redemption doc); dedup keys on
// voucherSourceId + code because that guard runs *before* check, when no redemptionId exists yet.
export function addRedeemedVoucher(redeemedVouchers, voucher) {
  const key = voucherKey(voucher);
  if (redeemedVouchers.some((v) => voucherKey(v) === key)) {
    throw new VoucherError('VOUCHER_ALREADY_ADDED');
  }
  return [...redeemedVouchers, voucher];
}

// The ids sent to the server: `voucherRedemptionIds` on the price call, `voucherRedemptions` on the
// order. The raw bearer code is never sent again — the backend already holds it on the redemption doc.
export function voucherRedemptionIds(redeemedVouchers) {
  return (redeemedVouchers || []).map((v) => v.redemptionId).filter(Boolean);
}

// Credential fields for the launch providers — the fallback when the backend hasn't exposed each
// adapter's credentialSchema yet. Adding provider #N is one adapter file on the backend; once its
// credentialSchema is served, the admin form renders from it with no change here.
export const KNOWN_PROVIDER_CREDENTIALS = {
  icmega: [{ name: 'sapakId', required: true }],
  tmura: [{ name: 'apiKey', required: true }],
  // Digitel (#1236): the subscription key rides as the Ocp-Apim-Subscription-Key header and the
  // institution code (קוד מוסד) identifies the seller to the city. Both required — without them the
  // club is unusable, and the provider dropdown is a closed select, so a club can't be added at all
  // until its provider is listed here.
  digitel: [
    { name: 'subscriptionKey', required: true },
    { name: 'institutionCode', required: true },
  ],
};

// Options for the club dialog's provider dropdown. The admin providers route isn't implemented yet,
// so the fetched list is empty — fall back to the known launch providers, or a club can't be added.
// The current value is always included so editing a club with a legacy/other provider stays valid.
export function providerOptionsFor(providers, currentValue) {
  const fromApi = (Array.isArray(providers) ? providers : []).map((p) => p.providerType).filter(Boolean);
  const base = fromApi.length ? fromApi : Object.keys(KNOWN_PROVIDER_CREDENTIALS);
  return Array.from(new Set([...base, currentValue].filter(Boolean)));
}

// The credential form is generic: prefer the provider's credentialSchema (JSON Schema properties),
// fall back to the known launch providers. Provider #N with a new field needs no admin-UI change.
export function credentialFieldsForProvider(providers, providerType) {
  const schema = (providers || []).find((p) => p.providerType === providerType)?.credentialSchema;
  if (schema?.properties) {
    const required = new Set(schema.required || []);
    return Object.keys(schema.properties).map((name) => ({ name, required: required.has(name) }));
  }
  return KNOWN_PROVIDER_CREDENTIALS[providerType] || [];
}

// Only send credential fields the user actually typed. An empty field must never overwrite the
// stored secret — the backend's write-only-secret guard is the real enforcement; this avoids even
// sending blanks (and matches how InvoiceSettings handles the masked token).
export function nonEmptyCredentials(credentials) {
  const out = {};
  for (const [key, value] of Object.entries(credentials || {})) {
    const cleaned = typeof value === 'string' ? value.trim() : value;
    if (cleaned) out[key] = cleaned;
  }
  return out;
}

// Discount rules mirror the BE validation (percent integer 1–100, fixed > 0). Anything else —
// including type "free" — is OMITTED: absent means "free / ₪0", the BE default, so legacy docs
// and legacy payloads stay byte-identical.
function cleanVariantDiscount(discount) {
  const type = discount?.type;
  if (type !== 'percent' && type !== 'fixed') return undefined;
  const value = Number(discount.value);
  if (!Number.isFinite(value) || value <= 0) return undefined;
  if (type === 'percent' && (!Number.isInteger(value) || value > 100)) return undefined;
  return { type, value };
}

// cleanExternalVariants OMITS an incomplete percent/fixed discount rather than rejecting the save —
// which means "producer picked a type but left/typo'd the value" saves silently as a free (₪0)
// mapping. The editor must catch that BEFORE save, so it needs the same validity check exposed here.
export function isValidVariantDiscount(discount) {
  const type = discount?.type;
  if (type !== 'percent' && type !== 'fixed') return true; // free/absent saves as ₪0 by intent
  return Boolean(cleanVariantDiscount(discount));
}

// Two rules the BACKEND enforces with a 400 that the editor cannot recover from gracefully (the
// ticket-save catch shows one generic toast for the whole batch), so the editor blocks them first:
//   - a digitel variantCode is the externalBenefitDateId registered with the city, capped at 10 chars
//   - each digitel row is reported and capped independently, so a ticket may carry at most one
export const DIGITEL_VARIANT_CODE_MAX = 10;

// The provider field is a freeSolo Autocomplete, so "Digitel" / " digitel " are typeable. The
// backend matches providerType against lowercase registry keys, so a stray capital silently
// becomes an unknown provider — and would slip past every check below. Canonicalize once, here.
export function normalizeProviderType(providerType) {
  return (providerType || '').toString().trim().toLowerCase();
}

export function variantCodeTooLong(row) {
  if (normalizeProviderType(row?.providerType) !== 'digitel') return false;
  return (row?.variantCode || '').toString().trim().length > DIGITEL_VARIANT_CODE_MAX;
}

// Returns the indexes of the SURPLUS digitel rows (every one after the first), so the editor can
// flag exactly the rows the producer has to remove.
export function surplusDigitelRowIndexes(rows) {
  const out = [];
  let seenDigitel = false;
  (rows || []).forEach((row, index) => {
    if (normalizeProviderType(row?.providerType) !== 'digitel') return;
    if (seenDigitel) out.push(index);
    seenDigitel = true;
  });
  return out;
}

// cleanExternalVariants silently omits a malformed cap and lets the BE default of 1 apply, so a
// typo'd "0" or "2.5" would vanish without telling the producer. The editor blocks it instead.
export function isValidVariantMaxQty(maxQtyPerCode) {
  if (maxQtyPerCode === '' || maxQtyPerCode === null || maxQtyPerCode === undefined) return true;
  const value = Number(maxQtyPerCode);
  return Number.isInteger(value) && value >= 1;
}

// A ticket's externalVariants map a provider's SKU to this Nimi ticket: [{ providerType,
// variantCode, discount?, maxQtyPerCode? }]. Clean on save — trim, drop incomplete rows, dedup
// within the ticket, and keep only well-formed discount/cap fields. (The backend also enforces
// that a pair maps at most one ticket per event.)
export function cleanExternalVariants(rows) {
  const seen = new Set();
  const out = [];
  for (const row of rows || []) {
    const providerType = normalizeProviderType(row.providerType);
    const variantCode = (row.variantCode || '').trim();
    if (!providerType || !variantCode) continue;
    const key = `${providerType}::${variantCode}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const cleaned = { providerType, variantCode };
    const discount = cleanVariantDiscount(row.discount);
    if (discount) cleaned.discount = discount;
    // Per-ID cap for eligibility-only clubs. Omitted when empty/invalid — the BE defaults to 1.
    const maxQtyPerCode = Number(row.maxQtyPerCode);
    if (Number.isInteger(maxQtyPerCode) && maxQtyPerCode >= 1) cleaned.maxQtyPerCode = maxQtyPerCode;
    out.push(cleaned);
  }
  return out;
}

// The cashier cart is multi-event, but a voucher validates against ONE eventId — a Zoo barcode
// must not discount the Aquarium line. So redemption attaches to the cart line for its own event.
export function addVoucherToCartItem(cart, eventId, voucher) {
  return cart.map((item) =>
    item.eventId === eventId
      ? { ...item, redeemedVouchers: addRedeemedVoucher(item.redeemedVouchers || [], voucher) }
      : item
  );
}

// --- orders table + export: read the denormalized voucher snapshot off the event-order ----------
// The backend stamps `event-order.voucherSourcesSnapshot` = [{ _id, displayName, providerType, code }]
// at check time, so the orders list can label a redeemed voucher without joining to voucher-redemption.
// `displayName` is the club name ("בזק - מועדון"); it falls back to the raw providerType if a legacy
// order predates the name snapshot. One entry per redeemed source in the order.

function voucherSnapshotLabel(entry) {
  const name = entry?.displayName || entry?.providerType || '';
  const code = entry?.code || '';
  if (name && code) return `${name}: ${code}`;
  return name || code;
}

export function voucherChipsFromSnapshot(voucherSourcesSnapshot) {
  if (!Array.isArray(voucherSourcesSnapshot)) return [];
  // Key on source id + code: a family can redeem two barcodes from the same club, so the source
  // _id alone isn't unique. The code disambiguates; index is the last-resort fallback.
  return voucherSourcesSnapshot.map((entry, index) => ({
    key: `${entry?._id || 'voucher'}::${entry?.code || index}`,
    label: voucherSnapshotLabel(entry),
  }));
}

export function voucherExportSummary(voucherSourcesSnapshot) {
  if (!Array.isArray(voucherSourcesSnapshot)) return '';
  return voucherSourcesSnapshot.map(voucherSnapshotLabel).filter(Boolean).join('; ');
}

// A valid code can still cover none of this EVENT's tickets — the check returns 200 with
// { matched: [], unmatched: [...] } and NO error field. The backend matches the voucher's variant
// codes against the event's whole ticket catalog (not the cart selection), so an empty `matched`
// means no ticket on this event maps these variants at all. `matched` is the only signal that a
// discount actually applied; without it the voucher must not attach, or the buyer sees a
// "benefit" that discounts nothing.
export function voucherCheckMatched(result) {
  return Array.isArray(result?.matched) && result.matched.length > 0;
}

// The error for that no-match case (#995): since matching runs against the event's full catalog,
// matched:[] means the code belongs to another event/venue (or its variants aren't configured
// here) — never "you didn't select that ticket", which sends the cashier re-picking tickets that
// can never match. Carry the provider's item desc when it exists so the cashier sees what the
// code actually covers (e.g. an Aquarium adult entry entered on the Zoo event).
export function voucherNoMatchError(result) {
  const desc = (result?.unmatched || []).map((row) => row?.desc).find(Boolean) || '';
  if (desc) {
    return { key: 'voucher_not_for_this_event_with_desc', params: { ticketName: desc } };
  }
  return { key: 'voucher_not_for_this_event', params: {} };
}

// --- #1039: coverage-vs-selection mismatch -------------------------------------------------
// The check matches the voucher against the event's CATALOG, so it passes even when the buyer's
// cart contains none of the covered tickets. Pricing then discounts nothing, but the redemption
// still rides the order and the whole barcode is burned at finalize (IC Mega: all-or-nothing,
// irreversible). These helpers compare the check's `matched` rows against the buyer's current
// selection so the dialog can warn BEFORE attaching.
// Coverage rows aggregated per ticket. Tmura returns one matched row per provider row, so a
// single voucher (or several pooled ones) can carry duplicate ticketIds — every consumer that
// SHOWS or COMPARES coverage must see the summed quantity, or "1× X, 1× X" lies about "2× X".
export function aggregateVoucherCoverage(matched) {
  const byTicket = new Map();
  for (const m of matched || []) {
    const ticketId = String(m.ticketId ?? m.ticket ?? '');
    byTicket.set(ticketId, (byTicket.get(ticketId) || 0) + (Number(m.qty) || 0));
  }
  return Array.from(byTicket, ([ticketId, qty]) => ({ ticketId, qty }));
}

// The HTTP check response carries `ticketId`; the persisted doc uses `ticket` — accept both.
export function voucherSelectionMismatch(matched, selectionQty) {
  const selection = selectionQty || {};
  // Tmura's check returns one row per provider item, so the same ticketId can appear multiple
  // times in `matched` — aggregate coverage per ticket BEFORE comparing, or two 1-qty rows each
  // "pass" against a 1-qty selection while the true combined coverage (2) exceeds it.
  return aggregateVoucherCoverage(matched)
    .map(({ ticketId, qty }) => ({
      ticketId,
      coveredQty: qty,
      selectedQty: Number(selection[ticketId]) || 0,
    }))
    .filter((row) => row.coveredQty > row.selectedQty);
}

// #1042: submit-time drift guard. The attach-time warning compares ONE voucher against the cart
// at redeem time; by pay time the cart may have changed (back-nav edits) and SEVERAL vouchers may
// be attached. Pool every attached voucher's matched rows and compare the aggregate against the
// final selection — voucherSelectionMismatch already aggregates per ticketId, so two vouchers
// each covering 1× of a ticket correctly demand 2× selected.
export function redeemedVouchersMismatch(redeemedVouchers, selectionQty) {
  const allMatched = (redeemedVouchers || [])
    // Eligibility-only clubs: `matched` is the remaining per-ID cap, not a voucher that burns
    // whole — unselected coverage costs the buyer nothing, so it must not warn (#1236).
    .filter((v) => !isEligibilityOnlyProvider(v.providerType))
    .flatMap((v) => v.matched || []);
  return voucherSelectionMismatch(allMatched, selectionQty);
}

// Event page selection: checkout.store's chosenTickets is { [ticketId]: { amount } }.
export function selectionFromChosenTickets(chosenTickets) {
  const out = {};
  for (const [ticketId, value] of Object.entries(chosenTickets || {})) {
    if (value?.amount) out[ticketId] = value.amount;
  }
  return out;
}

// Cashier selection: a cart line's tickets are [{ ticketId, name, qty, price }].
export function selectionFromCartTickets(cartTickets) {
  const out = {};
  for (const t of cartTickets || []) {
    if (t?.ticketId && t.qty) out[t.ticketId] = (out[t.ticketId] || 0) + t.qty;
  }
  return out;
}

// A voucher code is a REDEEMABLE bearer credential — the chip shows only the last 4 characters
// (same policy as the backend's per-ticket marker masking).
export function maskVoucherCode(code) {
  const s = String(code || '');
  return s.length <= 4 ? s : `****${s.slice(-4)}`;
}

export function ticketNameById(tickets, ticketId) {
  const id = String(ticketId ?? '');
  return (tickets || []).find((t) => String(t._id) === id)?.name || '';
}

// Chip label for an applied voucher: "<club> ****<last4> — 2× <ticket>, 1× <ticket>".
// Degrades gracefully for legacy entries that only carry the code (attached before #1039).
export function redeemedVoucherChipLabel(voucher, tickets) {
  const club = voucher?.sourceDisplayName || '';
  const masked = maskVoucherCode(voucher?.code);
  const covered = aggregateVoucherCoverage(voucher?.matched)
    .map(({ ticketId, qty }) => {
      const name = ticketNameById(tickets, ticketId);
      return name ? `${qty}× ${name}` : '';
    })
    .filter(Boolean)
    .join(', ');
  const head = [club, masked].filter(Boolean).join(' ');
  return covered ? `${head} — ${covered}` : head;
}

// Removal is pure FE: the entry's redemptionId is simply never sent on the price call or the
// order, so the backend's `checked` redemption doc is never committed and never burned.
export function removeRedeemedVoucher(redeemedVouchers, redemptionId) {
  // Without an id to match, filtering by !== would strip every entry that also lacks one.
  if (!redemptionId) return redeemedVouchers || [];
  return (redeemedVouchers || []).filter((v) => v.redemptionId !== redemptionId);
}
